import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Platform, ScrollView, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import Checkmark from 'assets/images/svgs/ico_checkmark.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectAppState, selectLayoutConfig } from 'store/config/selectors';
import {
    editProfileRequestReset,
    updateRatingsThresholdsRequest,
} from 'store/user/actions';
import { ParentalControlRating } from 'store/user/reducer.types';
import {
    selectActiveUserProfile,
    selectEditProfileErrorCode,
    selectEditProfileErrorStatus,
    selectProfilesList,
} from 'store/user/selectors';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import useNoInternet from 'utils/hooks/useNoInternet';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import { isWeb } from 'utils/responsiveness';

import {
    DIALOG_ERR_GENERIC_BODY,
    DIALOG_ERR_GENERIC_TITLE,
} from 'screens/Collection/constants';
import { deletedProfileErrorCodesArray } from 'screens/Profiles/utils';

import ErrorDialog from 'components/ErrorDialog';

import SettingsRowItem from '../components/RowItem';
import WebHeader from '../components/WebHeader';
import {
    SETTINGS_PARENTAL_CONTROL,
    SETTINGS_PARENTAL_CONTROL_6,
    SETTINGS_PARENTAL_CONTROL_12,
    SETTINGS_PARENTAL_CONTROL_16,
    SETTINGS_PARENTAL_CONTROL_18,
    SETTINGS_PARENTAL_CONTROL_ADULTS_CENSORED,
    SETTINGS_PARENTAL_CONTROL_ADULTS_UNCENSORED,
} from '../languagesConst';
import { SimpleSettingsItem } from '../types';
import UpdateRatingsThresholdsDialog from './components/UpdateRatingsThresholdsDialog';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.PARENTAL_CONTROL
    >;
};

const {
    PROFILE: {
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
    },
    OK_BUTTON,
    BACK_BUTTON,
} = LANGUAGE_KEYS;

function ParentalControl({ navigation }: Props) {
    const { t } = useTranslation();

    useHeaderConfig({ isRoot: isWeb, disableFocusOnMount: true });

    const [dialogVisible, setDialogVisible] = useState(false);
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [selectedRating, setSelectedRating] = useState<
        ParentalControlRating | undefined
    >(undefined);

    const { background } = useSelector(selectLayoutConfig);
    const appState = useSelector(selectAppState);
    const { primary } = useSelector(selectLayoutConfig);
    const editProfileErrorCode = useSelector(selectEditProfileErrorCode);
    const editProfileErrorStatus = useSelector(selectEditProfileErrorStatus);
    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);

    const profileParentalControlRating = useProfileParentalRating();
    const { isInternetConnected } = useNoInternet();
    const dispatch = useDispatch();

    const currentProfile = useMemo(
        () => profiles.find(({ _id }) => _id === selectedProfileId),
        [profiles, selectedProfileId],
    );

    const adultMenuOptions = useMemo<SimpleSettingsItem[]>(
        () => [
            {
                id: ParentalControlRating.HU18,
                title: t(SETTINGS_PARENTAL_CONTROL_18),
            },
            {
                id: ParentalControlRating.HU18_ADULT_CENSORED,
                title: t(SETTINGS_PARENTAL_CONTROL_ADULTS_CENSORED),
            },
            {
                id: ParentalControlRating.HU18_ADULT_UNCENSORED,
                title: t(SETTINGS_PARENTAL_CONTROL_ADULTS_UNCENSORED),
            },
        ],
        [t],
    );

    const menuOptions = useMemo<SimpleSettingsItem[]>(
        () => [
            {
                id: ParentalControlRating.HU6,
                title: t(SETTINGS_PARENTAL_CONTROL_6),
            },
            {
                id: ParentalControlRating.HU12,
                title: t(SETTINGS_PARENTAL_CONTROL_12),
            },
            {
                id: ParentalControlRating.HU16,
                title: t(SETTINGS_PARENTAL_CONTROL_16),
            },
            ...(!currentProfile?.childProfile ? adultMenuOptions : []),
        ],
        [t, currentProfile, adultMenuOptions],
    );

    const errorContent = useMemo(() => {
        if (currentProfile?.childProfile) {
            return {
                title: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
                description: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
                actionButtonText: OK_BUTTON,
            };
        }

        if (deletedProfileErrorCodesArray.includes(editProfileErrorCode)) {
            return {
                title: PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
                description: PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
                actionButtonText: OK_BUTTON,
            };
        }

        return {
            title: DIALOG_ERR_GENERIC_TITLE,
            description: DIALOG_ERR_GENERIC_BODY,
            actionButtonText: BACK_BUTTON,
        };
    }, [currentProfile, editProfileErrorCode]);

    const handlePress = useCallback(
        ({ id }: SimpleSettingsItem) => {
            if (profileParentalControlRating !== id) {
                setSelectedRating(id as ParentalControlRating);
                setDialogVisible(true);
            }
        },
        [profileParentalControlRating],
    );

    const closeDialog = useCallback(() => {
        setDialogVisible(false);
    }, []);

    const handleCloseErrorDialog = useCallback(() => {
        if (editProfileErrorCode === 8128) {
            dispatch(updateRatingsThresholdsRequest());
            navigation.reset({
                routes: [{ name: SCREEN.PROFILES }],
            });
        } else {
            navigation.goBack();
        }

        setErrorDialogVisible(false);
    }, [editProfileErrorCode, navigation]);

    useFocusEffect(
        useCallback(
            () => () => {
                if (editProfileErrorCode) {
                    dispatch(editProfileRequestReset());
                }
            },
            [editProfileErrorCode],
        ),
    );

    useEffect(() => {
        if (editProfileErrorCode) {
            setTimeout(() => {
                setDialogVisible(false);
            }, 100);
            setTimeout(() => {
                setErrorDialogVisible(true);
            }, Platform.select({ ios: 750, default: 100 }));
        }
    }, [editProfileErrorCode]);

    return (
        <View
            style={[gStyles.settingsContainer, { backgroundColor: background }]}
        >
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_PARENTAL_CONTROL)}
                    disableFocusOnMount
                />
            )}
            <ScrollView
                style={[
                    gStyles.settingsContentContainer,
                    { backgroundColor: background },
                ]}
                nestedScrollEnabled
                showsHorizontalScrollIndicator={false}
                accessibilityRole="menu"
            >
                {menuOptions.map((item, index) => (
                    <SettingsRowItem
                        key={item.id}
                        item={item}
                        title={item.title}
                        RightIcon={
                            profileParentalControlRating === item.id
                                ? Checkmark
                                : undefined
                        }
                        onPress={handlePress}
                        position={index + 1}
                        total={menuOptions.length}
                        isSelected={profileParentalControlRating === item.id}
                    />
                ))}
            </ScrollView>
            <UpdateRatingsThresholdsDialog
                visible={dialogVisible}
                selectedRating={selectedRating}
                appState={appState}
                onClose={closeDialog}
            />

            <ErrorDialog
                visible={isInternetConnected && errorDialogVisible}
                animationType="fade"
                title={errorContent.title}
                description={errorContent.description}
                actionButtonTitle={errorContent.actionButtonText}
                actionButtonAccessibilityLabel={t(
                    errorContent.actionButtonText,
                )}
                actionButtonOnPress={handleCloseErrorDialog}
                errorCode={`${editProfileErrorCode || ''}`}
                actionButtonFillColor={primary}
                actionButtonTextStyle={styles.buttonText}
                actionButtonStyle={[styles.button, { borderColor: primary }]}
                errorStatus={editProfileErrorStatus}
                actionButtonBordered
            />
        </View>
    );
}

export default ParentalControl;
